import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useSeller } from '../contexts/SellerContext';
import PageHeader from '../components/PageHeader';
import './SellerProfile.css';

const SellerProfile = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { seller, updateSeller } = useSeller();

  // Profile data state
  const [profileData, setProfileData] = useState({
    // Basic info from seller context
    sellerId: '',
    mobile: '',
    segment: '',
    // Business step data
    gstrStatus: '',
    gstinNumber: '',
    organizationName: '',
    organizationContact: '',
    organizationEmail: '',
    businessType: '',
    teamSize: '',
    businessDocumentUrls: [],
    organizationPhotoUrls: [],
    storeLogoUrl: '',
    // Person step data
    dealingPersons: [],
    // Contact step data
    transactionalMobile: '',
    transactionalEmail: '',
    shopNumber: '',
    buildingName: '',
    streetAddress: '',
    contactCity: '',
    contactDistrict: '',
    contactState: '',
    contactPinCode: '',
    shippingLatitude: '',
    shippingLongitude: '',
    shippingLocation: '',
    // Bank step data
    bankName: '',
    ifscCode: '',
    accountType: '',
    accountHolderName: '',
    accountNumber: '',
    confirmAccountNumber: '',
    // Security step data
    jmiOfficerID: '',
    privatePasskey: '',
    confirmPasskey: '',
  });

  const [originalData, setOriginalData] = useState(null);

 

  // Filter dealing persons to only those with fullName for display
  // const editableDealingPersons = profileData.dealingPersons?.filter(person => person && person.fullName) || [];
  useEffect(() => {
    const loadAllData = async () => {
      if (!seller || !seller.sellerId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Fetch from sellerregistrations collection
        const regRef = doc(db, 'sellerregistrations', seller.sellerId);
        const regSnap = await getDoc(regRef);

        // Also fetch from sellerProfiles collection if exists
        const profileRef = doc(db, 'sellerProfiles', seller.sellerId);
        const profileSnap = await getDoc(profileRef);

        // Fetch segment registration if exists
        const segmentRef = doc(db, 'SellerSegmentRegistrations', seller.sellerId);
        const segmentSnap = await getDoc(segmentRef);

        let mergedData = {
          sellerId: seller.sellerId,
          mobile: seller.mobile || '',
          segment: seller.segment || '',
          gstrStatus: '',
          gstinNumber: '',
          organizationName: '',
          organizationContact: '',
          organizationEmail: '',
          businessType: '',
          teamSize: '',
          businessDocumentUrls: [],
          organizationPhotoUrls: [],
          storeLogoUrl: '',
          dealingPersons: [],
          transactionalMobile: '',
          transactionalEmail: '',
          shopNumber: '',
          buildingName: '',
          streetAddress: '',
          contactCity: '',
          contactDistrict: '',
          contactState: '',
          contactPinCode: '',
          shippingLatitude: '',
          shippingLongitude: '',
          shippingLocation: '',
          bankName: '',
          ifscCode: '',
          accountType: '',
          accountHolderName: '',
          accountNumber: '',
          confirmAccountNumber: '',
          jmiOfficerID: '',
          privatePasskey: '',
          confirmPasskey: '',
        };

        if (regSnap.exists()) {
          mergedData = { ...mergedData, ...regSnap.data() };
        }
        if (profileSnap.exists()) {
          mergedData = { ...mergedData, ...profileSnap.data() };
        }
        if (segmentSnap.exists()) {
          const segmentData = segmentSnap.data();
          mergedData.segment = segmentData.segment || mergedData.segment;
          mergedData.operatorMobile = segmentData.operatorMobile || '';
          mergedData.operatorId = segmentData.operatorId || '';
          mergedData.operatorName = segmentData.operatorName || segmentData.operatorOrganizationName || '';
        }

        // Resolve operator display name: prefer stored name, else fetch from operatorregistrations
        if (mergedData.operatorId && !mergedData.operatorName) {
          try {
            const opSnap = await getDoc(doc(db, 'operatorregistrations', mergedData.operatorId));
            if (opSnap.exists()) {
              const d = opSnap.data();
              mergedData.operatorName = d.organizationName || d.businessName || d.operatorName || d.name || '';
            }
          } catch (e) {
            console.warn('Failed to resolve operator name', e);
          }
        }

        setProfileData(mergedData);
        setOriginalData(mergedData);
      } catch (error) {
        console.error('Error loading profile data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAllData();
  }, [seller]);

  // Handle input changes
  const handleInputChange = useCallback((field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Handle dealing person changes
  const handleDealingPersonChange = useCallback((index, field, value) => {
    setProfileData(prev => {
      const updated = [...prev.dealingPersons];
      if (updated[index]) {
        updated[index] = { ...updated[index], [field]: value };
      }
      return { ...prev, dealingPersons: updated };
    });
  }, []);

  // Check if form has changes
  const hasChanges = () => {
    if (!originalData) return false;
    return JSON.stringify(profileData) !== JSON.stringify(originalData);
  };

  // Reset form to original data
  const resetForm = () => {
    if (originalData) {
      setProfileData(originalData);
      setIsEditing(false);
      setSaveStatus(null);
    }
  };

  // Save profile data
  const saveProfile = async () => {
    if (!seller || !seller.sellerId) return;

    setSaveStatus('saving');
    try {
      const regRef = doc(db, 'sellerregistrations', seller.sellerId);
      const profileRef = doc(db, 'sellerProfiles', seller.sellerId);

      // Save to sellerregistrations (main registration data)
      await updateDoc(regRef, profileData);

      // Also save to sellerProfiles
      await updateDoc(profileRef, profileData);

      setOriginalData(profileData);
      setIsEditing(false);
      setSaveStatus('success');

      // Update seller context with phone if changed
      if (profileData.mobile && profileData.mobile !== seller.mobile) {
        updateSeller({
          mobile: profileData.mobile,
        });
      }

      setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    } catch (error) {
      console.error('Error saving profile data:', error);
      setSaveStatus('error');
      setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    }
  };

  // Confirm reset changes
  const confirmReset = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmReset = () => {
    setShowConfirmDialog(false);
    resetForm();
  };

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return 'JM';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (isLoading) {
    return (
      <>
        <PageHeader title="Seller Profile" />
        <div className="sp-loading">
          <div className="loading-spinner"></div>
          <p>Loading your profile...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Seller Profile" />
      <div className="sp-page">
        {/* Hero Card */}
        <div className="sp-hero-card">
          <div className="sp-hero-left">
            <div className="sp-avatar-circle">
              {profileData.storeLogoUrl ? (
                <img src={profileData.storeLogoUrl} alt="Logo" className="sp-avatar-img" />
              ) : (
                getInitials(profileData.organizationName)
              )}
            </div>
            <div className="sp-hero-info">
              <h1 className="sp-org-name">
                {profileData.organizationName || 'Organization Name'}
              </h1>
              <div className="sp-hero-sub">
                <span className="sp-buyer-id-badge">
                  {profileData.sellerId || 'No ID'}
                </span>
                {profileData.segment && (
                  <span className="sp-buyer-id-badge">
                    {profileData.segment} Segment
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="sp-actions">
            {isEditing ? (
              <>
                <button
                  className="btn btn-outline sp-save-btn"
                  onClick={confirmReset}
                  disabled={!hasChanges()}
                >
                  Cancel
                </button>
                <button
                  className="btn sp-save-btn"
                  onClick={saveProfile}
                  disabled={!hasChanges()}
                >
                  Save Changes
                </button>
              </>
            ) : (
              <button
                className="btn sp-edit-btn"
                onClick={() => setIsEditing(true)}
              >
                Edit Profile
              </button>
            )}
          </div>
        </div>

        {saveStatus === 'success' && (
          <div className="alert alert-success">
            Profile saved successfully!
          </div>
        )}

        {saveStatus === 'error' && (
          <div className="alert alert-error">
            Error saving profile. Please try again.
          </div>
        )}

        <form className="sp-form" onSubmit={(e) => e.preventDefault()}>
          <div className="sp-grid">
            {/* Business Information Card */}
            <div className="sp-card">
              <div className="sp-card-header">
                <span className="sp-card-icon">🏢</span>
                Business Information
              </div>
              <div className="sp-field-list">
                <div className="sp-field-item">
                  <span className="sp-field-label">GSTR Status</span>
                  {isEditing ? (
                    <select
                      className="sp-field-input"
                      value={profileData.gstrStatus || ''}
                      onChange={(e) => handleInputChange('gstrStatus', e.target.value)}
                    >
                      <option value="">Select GSTR Status</option>
                      <option value="registered">Registered</option>
                      <option value="non-registered">Non-Registered</option>
                    </select>
                  ) : (
                    <span className="sp-field-value">
                      {profileData.gstrStatus === 'registered' ? 'Registered' :
                        profileData.gstrStatus === 'non-registered' ? 'Non-Registered' : 'Not Set'}
                    </span>
                  )}
                </div>

                <div className="sp-field-item">
                  <span className="sp-field-label">
                    {profileData.gstrStatus === 'non-registered' ? 'PAN Number' : 'GSTIN Number'}
                  </span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="sp-field-input"
                      value={profileData.gstinNumber || ''}
                      onChange={(e) => handleInputChange('gstinNumber', e.target.value.toUpperCase())}
                      maxLength={profileData.gstrStatus === 'non-registered' ? 10 : 15}
                      placeholder={profileData.gstrStatus === 'non-registered' ? "e.g. ABCDE1234F" : "e.g. 22AAAAA0000A1Z5"}
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.gstinNumber || 'Not Set'}
                    </span>
                  )}
                </div>

                <div className="sp-field-item">
                  <span className="sp-field-label">Organization Name</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="sp-field-input"
                      value={profileData.organizationName || ''}
                      onChange={(e) => handleInputChange('organizationName', e.target.value)}
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.organizationName || 'Not Set'}
                    </span>
                  )}
                </div>

                <div className="sp-field-item">
                  <span className="sp-field-label">Organization Contact</span>
                  {isEditing ? (
                    <input
                      type="tel"
                      className="sp-field-input"
                      value={profileData.organizationContact || ''}
                      onChange={(e) => handleInputChange('organizationContact', e.target.value)}
                      maxLength="10"
                      inputMode="numeric"
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.organizationContact || 'Not Set'}
                    </span>
                  )}
                </div>

                <div className="sp-field-item">
                  <span className="sp-field-label">Organization Email</span>
                  {isEditing ? (
                    <input
                      type="email"
                      className="sp-field-input"
                      value={profileData.organizationEmail || ''}
                      onChange={(e) => handleInputChange('organizationEmail', e.target.value)}
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.organizationEmail || 'Not Set'}
                    </span>
                  )}
                </div>

                <div className="sp-field-item">
                  <span className="sp-field-label">Business Type</span>
                  {isEditing ? (
                    <select
                      className="sp-field-input"
                      value={profileData.businessType || ''}
                      onChange={(e) => handleInputChange('businessType', e.target.value)}
                    >
                      <option value="">Select Business Type</option>
                      <option value="karigar">Karigar</option>
                      <option value="super_stockist">Super Stockist</option>
                      <option value="c_and_f">C&F</option>
                      <option value="Manufacturer">Manufacturer</option>
                      <option value="wholesaler">Wholesaler</option>
                      <option value="broker">Broker</option>
                      <option value="corporate_b2b">Corporate B2B</option>
                      <option value="corporate_D2b">Corporate D2B</option>
                    </select>
                  ) : (
                    <span className="sp-field-value">
                      {profileData.businessType || 'Not Set'}
                    </span>
                  )}
                </div>

                <div className="sp-field-item">
                  <span className="sp-field-label">Team Size</span>
                  {isEditing ? (
                    <input
                      type="number"
                      className="sp-field-input"
                      value={profileData.teamSize || ''}
                      onChange={(e) => handleInputChange('teamSize', e.target.value)}
                      min="0"
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.teamSize || 'Not Set'}
                    </span>
                  )}
                </div>

                <div className="sp-field-item">
                  <span className="sp-field-label">Business Documents</span>
                  <span className="sp-field-value">
                    {profileData.businessDocumentUrls?.length > 0
                      ? `${profileData.businessDocumentUrls.length} document(s) uploaded`
                      : 'No documents uploaded'}
                  </span>
                </div>

                <div className="sp-field-item">
                  <span className="sp-field-label">Organization Photos</span>
                  <span className="sp-field-value">
                    {profileData.organizationPhotoUrls?.length > 0
                      ? `${profileData.organizationPhotoUrls.length} photo(s) uploaded`
                      : 'No photos uploaded'}
                  </span>
                </div>

                <div className="sp-field-item">
                  <span className="sp-field-label">Store Logo</span>
                  <span className="sp-field-value">
                    {profileData.storeLogoUrl ? 'Uploaded' : 'Not uploaded'}
                  </span>
                </div>
              </div>
            </div>

            {/* Dealing Persons Card */}
            <div className="sp-card">
              <div className="sp-card-header">
                <span className="sp-card-icon">👥</span>
                Dealing Persons
              </div>
              <div className="sp-field-list">
                {profileData.dealingPersons?.length > 0 ? (
                  profileData.dealingPersons.map((person, index) => (
                    <div key={index} className="sp-person-card">
                      <div className="sp-person-avatar">
                        {person.photoUrl ? (
                          <img src={person.photoUrl} alt={person.fullName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          getInitials(person.fullName)
                        )}
                      </div>
                      <div className="sp-person-info">
                        <div className="sp-person-name">
                          {isEditing ? (
                            <input
                              type="text"
                              className="sp-field-input"
                              style={{ padding: '2px 8px', fontSize: '0.9rem', fontWeight: '700' }}
                              value={person.fullName || ''}
                              onChange={(e) => handleDealingPersonChange(index, 'fullName', e.target.value)}
                            />
                          ) : (
                            person.fullName || 'Unknown'
                          )}
                        </div>
                        <div className="sp-person-sub">
                          {person.role || ''} • {person.department || ''}
                        </div>
                        {isEditing && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
                            <input
                              type="tel"
                              className="sp-field-input"
                              value={person.contactNumber || ''}
                              onChange={(e) => handleDealingPersonChange(index, 'contactNumber', e.target.value)}
                              placeholder="Contact Number"
                              maxLength="10"
                              inputMode="numeric"
                            />
                            <input
                              type="email"
                              className="sp-field-input"
                              value={person.email || ''}
                              onChange={(e) => handleDealingPersonChange(index, 'email', e.target.value)}
                              placeholder="Email"
                            />
                            <select
                              className="sp-field-input"
                              value={person.department || ''}
                              onChange={(e) => handleDealingPersonChange(index, 'department', e.target.value)}
                            >
                              <option value="">Select department</option>
                              <option value="Adminstrative">Adminstrative</option>
                              <option value="Finance">Finance</option>
                              <option value="Hr">Hr</option>
                              <option value="R&D">R&D</option>
                              <option value="Sales&Marketing">Sales & Marketing</option>
                              <option value="Legal">Legal</option>
                              <option value="Accounting">Accounting</option>
                            </select>
                            <select
                              className="sp-field-input"
                              value={person.role || ''}
                              onChange={(e) => handleDealingPersonChange(index, 'role', e.target.value)}
                            >
                              <option value="">Select role</option>
                              <option value="ManagingDirector">Managing Director</option>
                              <option value="Director">Director</option>
                              <option value="Manager">Manager</option>
                              <option value="CEO">CEO</option>
                              <option value="CFO">CFO</option>
                              <option value="COO">COO</option>
                              <option value="ChiefSalesExecutive">Chief Sales Executive</option>
                              <option value="sales-executive">Sales Executive</option>
                              <option value="accountant">Accountant</option>
                              <option value="Employee">Employee</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="sp-field-item">
                    <span className="sp-field-value" style={{ color: 'var(--color-text-muted)' }}>
                      No dealing persons added
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="sp-grid">
            {/* Contact Information Card */}
            <div className="sp-card">
              <div className="sp-card-header">
                <span className="sp-card-icon">📞</span>
                Transactional Contact
              </div>
              <div className="sp-field-list">
                <div className="sp-field-item">
                  <span className="sp-field-label">Mobile</span>
                  {isEditing ? (
                    <input
                      type="tel"
                      className="sp-field-input"
                      value={profileData.transactionalMobile || ''}
                      onChange={(e) => handleInputChange('transactionalMobile', e.target.value)}
                      maxLength="10"
                      inputMode="numeric"
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.transactionalMobile || profileData.mobile || 'Not Set'}
                    </span>
                  )}
                </div>
                <div className="sp-field-item">
                  <span className="sp-field-label">Email</span>
                  {isEditing ? (
                    <input
                      type="email"
                      className="sp-field-input"
                      value={profileData.transactionalEmail || ''}
                      onChange={(e) => handleInputChange('transactionalEmail', e.target.value)}
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.transactionalEmail || 'Not Set'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Shipping Address Card */}
            <div className="sp-card">
              <div className="sp-card-header">
                <span className="sp-card-icon">📍</span>
                Shipping Address
              </div>
              <div className="sp-field-list">
                <div className="sp-field-item">
                  <span className="sp-field-label">Shop / Flat Number</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="sp-field-input"
                      value={profileData.shopNumber || ''}
                      onChange={(e) => handleInputChange('shopNumber', e.target.value)}
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.shopNumber || 'Not Set'}
                    </span>
                  )}
                </div>
                <div className="sp-field-item">
                  <span className="sp-field-label">Building / Complex</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="sp-field-input"
                      value={profileData.buildingName || ''}
                      onChange={(e) => handleInputChange('buildingName', e.target.value)}
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.buildingName || 'Not Set'}
                    </span>
                  )}
                </div>
                <div className="sp-field-item">
                  <span className="sp-field-label">Street Address</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="sp-field-input"
                      value={profileData.streetAddress || ''}
                      onChange={(e) => handleInputChange('streetAddress', e.target.value)}
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.streetAddress || 'Not Set'}
                    </span>
                  )}
                </div>
                <div className="sp-field-item">
                  <span className="sp-field-label">City / Village</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="sp-field-input"
                      value={profileData.contactCity || ''}
                      onChange={(e) => handleInputChange('contactCity', e.target.value)}
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.contactCity || 'Not Set'}
                    </span>
                  )}
                </div>
                <div className="sp-field-item">
                  <span className="sp-field-label">District</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="sp-field-input"
                      value={profileData.contactDistrict || ''}
                      onChange={(e) => handleInputChange('contactDistrict', e.target.value)}
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.contactDistrict || 'Not Set'}
                    </span>
                  )}
                </div>
                <div className="sp-field-item">
                  <span className="sp-field-label">State</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="sp-field-input"
                      value={profileData.contactState || ''}
                      onChange={(e) => handleInputChange('contactState', e.target.value)}
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.contactState || 'Not Set'}
                    </span>
                  )}
                </div>
                <div className="sp-field-item">
                  <span className="sp-field-label">District</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="sp-field-input"
                      value={profileData.contactDistrict || ''}
                      onChange={(e) => handleInputChange('contactDistrict', e.target.value)}
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.contactDistrict || 'Not Set'}
                    </span>
                  )}
                </div>
                <div className="sp-field-item">
                  <span className="sp-field-label">Pincode</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="sp-field-input"
                      value={profileData.contactPinCode || ''}
                      onChange={(e) => handleInputChange('contactPinCode', e.target.value)}
                      maxLength="6"
                      inputMode="numeric"
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.contactPinCode || 'Not Set'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="sp-grid">
            {/* Bank Details Card */}
            <div className="sp-card">
              <div className="sp-card-header">
                <span className="sp-card-icon">🏦</span>
                Bank Details
              </div>
              <div className="sp-field-list">
                <div className="sp-field-item">
                  <span className="sp-field-label">IFSC Code</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="sp-field-input"
                      value={profileData.ifscCode || ''}
                      onChange={(e) => handleInputChange('ifscCode', e.target.value.toUpperCase())}
                      maxLength="11"
                      placeholder="e.g. SBIN0001234"
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.ifscCode || 'Not Set'}
                    </span>
                  )}
                </div>
                <div className="sp-field-item">
                  <span className="sp-field-label">Bank Name</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="sp-field-input"
                      value={profileData.bankName || ''}
                      onChange={(e) => handleInputChange('bankName', e.target.value)}
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.bankName || 'Not Set'}
                    </span>
                  )}
                </div>
                <div className="sp-field-item">
                  <span className="sp-field-label">Account Type</span>
                  {isEditing ? (
                    <select
                      className="sp-field-input"
                      value={profileData.accountType || ''}
                      onChange={(e) => handleInputChange('accountType', e.target.value)}
                    >
                      <option value="">Select account type</option>
                      <option value="savings">Savings Account</option>
                      <option value="current">Current Account</option>
                      <option value="cc">Cash Credit</option>
                      <option value="od">Overdraft</option>
                    </select>
                  ) : (
                    <span className="sp-field-value">
                      {profileData.accountType || 'Not Set'}
                    </span>
                  )}
                </div>
                <div className="sp-field-item">
                  <span className="sp-field-label">Account Holder Name</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="sp-field-input"
                      value={profileData.accountHolderName || ''}
                      onChange={(e) => handleInputChange('accountHolderName', e.target.value)}
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.accountHolderName || 'Not Set'}
                    </span>
                  )}
                </div>
                <div className="sp-field-item">
                  <span className="sp-field-label">Account Number</span>
                  {isEditing ? (
                    <input
                      type="password"
                      className="sp-field-input"
                      value={profileData.accountNumber || ''}
                      onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.accountNumber ? '••••••••••' + profileData.accountNumber.slice(-4) : 'Not Set'}
                    </span>
                  )}
                </div>
                <div className="sp-field-item">
                  <span className="sp-field-label">Confirm Account Number</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="sp-field-input"
                      value={profileData.confirmAccountNumber || ''}
                      onChange={(e) => handleInputChange('confirmAccountNumber', e.target.value)}
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.confirmAccountNumber ? '••••••••••' + profileData.confirmAccountNumber.slice(-4) : 'Not Set'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Security Card */}
            <div className="sp-card">
              <div className="sp-card-header">
                <span className="sp-card-icon">🔐</span>
                Security
              </div>
              <div className="sp-field-list">
                <div className="sp-field-item">
                  <span className="sp-field-label">JMI Officer ID</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="sp-field-input"
                      value={profileData.jmiOfficerID || ''}
                      onChange={(e) => handleInputChange('jmiOfficerID', e.target.value)}
                      maxLength="10"
                      inputMode="numeric"
                    />
                  ) : (
                    <span className="sp-field-value">
                      {profileData.jmiOfficerID || 'Not Set'}
                    </span>
                  )}
                </div>
                <div className="sp-field-item">
                  <span className="sp-field-label">Private Passkey</span>
                  <span className="sp-field-value">
                    {profileData.privatePasskey ? '•••••• (6 digits)' : 'Not Set'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Segment Registration Card */}
          {(profileData.segment || profileData.operatorMobile || profileData.operatorId) && (
            <div className="sp-grid">
              <div className="sp-card">
                <div className="sp-card-header">
                  <span className="sp-card-icon">🤝</span>
                  Segment Registration
                </div>
                <div className="sp-field-list">
                  <div className="sp-segments-container">
                    <div className="sp-segment-pill">
                      <span className="sp-segment-title">
                        {profileData.segment ? profileData.segment.charAt(0).toUpperCase() + profileData.segment.slice(1) : 'Not Registered'}
                      </span>
                      {profileData.operatorMobile && (
                        <span className="sp-segment-operator">
                          Operator: {profileData.operatorName || profileData.operatorMobile}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showConfirmDialog && (
            <div className="confirm-dialog">
              <div className="confirm-dialog-content">
                <h3>Confirm Reset</h3>
                <p>Are you sure you want to discard all changes?</p>
                <div className="confirm-dialog-actions">
                  <button className="btn btn-outline" onClick={handleConfirmReset}>
                    Yes, Discard Changes
                  </button>
                  <button className="btn btn-secondary" onClick={() => setShowConfirmDialog(false)}>
                    No, Keep Editing
                  </button>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </>
  );
};

export default SellerProfile;
